import { ManagerRole } from '~/constants/clientTypes.ts';
import { jwtDecode } from 'jwt-decode';

const COVENANTS_OPERATOR_GROUPS = [
  ManagerRole.call_center_operator,
  ManagerRole.covenants_branch_manager,
];
const COVENANTS_ALLOWED_GROUPS = new Set([
  ...COVENANTS_OPERATOR_GROUPS,
  ManagerRole.uakdo_administrator,
]);
const MAX_MANAGER_GROUPS = 20;

const normalizeManagerGroup = (group: string): string =>
  group === ManagerRole.branch_manager ? ManagerRole.covenants_branch_manager : group;

export const normalizeManagerGroups = (groups?: unknown): ManagerRole[] =>
  Array.from(
    new Set(
      (Array.isArray(groups) ? groups : [])
        .slice(0, MAX_MANAGER_GROUPS)
        .map((group) => (typeof group === 'string' ? normalizeManagerGroup(group.trim()) : ''))
        .filter(
          (group): group is string => !!group && COVENANTS_ALLOWED_GROUPS.has(group as ManagerRole)
        )
    )
  ) as ManagerRole[];

const getTokenGroupsCandidate = (decodedToken: Record<string, unknown>) => {
  return (
    decodedToken.groups ??
    decodedToken.roles ??
    decodedToken.authorities ??
    decodedToken.group ??
    decodedToken.role ??
    null
  );
};

export const getManagerGroupsFromToken = (token?: string | null): string[] => {
  try {
    if (!token) {
      return [];
    }

    const decodedToken = jwtDecode<Record<string, unknown>>(token);
    const groupsCandidate = getTokenGroupsCandidate(decodedToken);

    return normalizeManagerGroups(groupsCandidate);
  } catch {
    return [];
  }
};

export const resolveManagerGroups = (groups?: unknown, token?: string | null): string[] => {
  const normalizedGroups = normalizeManagerGroups(groups);
  return normalizedGroups.length ? normalizedGroups : getManagerGroupsFromToken(token);
};

export const hasCovenantsManagerAccess = (groups?: string[] | null): boolean => {
  return Array.isArray(groups) && groups.includes(ManagerRole.uakdo_administrator);
};

export const hasCovenantsOperatorAccess = (groups?: ManagerRole[] | null): boolean => {
  return Array.isArray(groups) && groups.some((group) => COVENANTS_OPERATOR_GROUPS.includes(group));
};
